import { useState, useEffect } from "react";

/**
 * Generic data-fetching hook for the content service.
 *
 *   const { data, loading, error } = useResource(getSlides);
 *
 * - `fetcher` must be a STABLE reference (a named service function), so the
 *   effect doesn't re-run every render. Don't pass an inline arrow.
 * - Aborts the request on unmount / re-run to avoid setting state on a gone
 *   component.
 * - `fallback` seeds `data` before the first load resolves.
 */
export function useResource(fetcher, fallback = null) {
  const [data, setData] = useState(fallback);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const ctrl = new AbortController();
    let active = true;

    setLoading(true);
    setError(null);

    fetcher({ signal: ctrl.signal })
      .then((d) => {
        if (active) {
          setData(d);
          setLoading(false);
        }
      })
      .catch((e) => {
        if (active && e.name !== "AbortError") {
          setError(e);
          setLoading(false);
        }
      });

    return () => {
      active = false;
      ctrl.abort();
    };
  }, [fetcher]);

  return { data, loading, error };
}
